import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Trophy, Calendar, Eye, Edit, Plus, AlertCircle, X } from 'lucide-react';
import { teamAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Team {
  _id: string;
  name: string;
  hackathon: {
    _id: string;
    title: string;
    startDate: string;
    endDate: string;
    status: string;
  };
  leader: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  members: Array<{
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    role: string;
    joinedAt: string;
  }>;
  selectedProblemStatement?: {
    title: string;
    description: string;
    category: string;
    difficulty: string;
  };
  assignedProblemStatement?: {
    title: string;
    description: string;
    category: string;
    difficulty: string;
  };
  status: string;
  isApproved: boolean;
  isActive: boolean;
  joinCode: string;
  submission?: {
    title: string;
    description: string;
    githubLink: string;
    demoLink: string;
    submittedAt: string;
  };
  createdAt: string;
}

const MyTeams: React.FC = () => {
  const { success, error } = useToast();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, isLoading: authLoading } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<{id: string, name: string} | null>(null);



  useEffect(() => {
    fetchMyTeams();
  }, []);

  const fetchMyTeams = async () => {
    try {
      setLoading(true);
      const response = await teamAPI.getMyTeams();
      setTeams(response);
    } catch (err: any) {

      error(err.response?.data?.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTeam = (teamId: string, teamName: string) => {
    setTeamToDelete({ id: teamId, name: teamName });
    setShowDeleteDialog(true);
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    try {
      await teamAPI.deleteTeam(teamToDelete.id);
      success('Team deleted successfully!');
      fetchMyTeams(); // Refresh the teams list
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete team');
    } finally {
      setShowDeleteDialog(false);
      setTeamToDelete(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'forming': return 'bg-yellow-100 text-yellow-800';
      case 'pending_approval': return 'bg-orange-100 text-orange-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'submitted': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'forming': return 'FORMING';
      case 'pending_approval': return 'PENDING APPROVAL';
      case 'approved': return 'APPROVED';
      case 'active': return 'ACTIVE';
      case 'submitted': return 'SUBMITTED';
      case 'completed': return 'COMPLETED';
      case 'rejected': return 'REJECTED';
      default: return status.replace('_', ' ').toUpperCase();
    }
  };

  const getHackathonStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'registration_open': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isTeamLeader = (team: Team) => {
    return team.leader._id === user?._id;
  };

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">

      
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Teams</h1>
            <p className="text-gray-600">Manage your hackathon teams and projects</p>
          </div>
          <Link to="/teams/create">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Create New Team
            </Button>
          </Link>
        </div>
      </div>

      {teams.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No teams yet</h3>
            <p className="text-gray-600 mb-4">
              You haven't joined any hackathon teams yet. Start by creating a team or joining an existing one.
            </p>
            <div className="space-x-4">
              <Link to="/hackathons">
                <Button variant="outline">Browse Hackathons</Button>
              </Link>
              <Link to="/teams/create">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white">Create Team</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {teams.map((team) => (
            <Card key={team._id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl font-bold mb-3 text-gray-900">{team.name}</CardTitle>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${getStatusColor(team.status)} font-semibold text-sm px-3 py-1`}>
                        {getStatusDisplayText(team.status)}
                      </Badge>
                      {/* Only show isActive badge if it provides different information than status */}
                      {(team.status !== 'active' || !team.isActive) && (
                        <Badge className={`${team.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} font-semibold text-sm px-3 py-1`}>
                          {team.isActive ? 'ACTIVE' : 'INACTIVE'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Trophy className="h-6 w-6 text-yellow-500" />
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Hackathon Info */}
                {team.hackathon ? (
                  <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg">
                    <h4 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-500" />
                      {team.hackathon.title}
                    </h4>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={`${getHackathonStatusColor(team.hackathon.status || 'unknown')} font-medium`}>
                        {(team.hackathon.status || 'unknown').replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-700 bg-white p-2 rounded border">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium">Duration:</span>
                        <span>{formatDate(team.hackathon.startDate)} at {formatTime(team.hackathon.startDate)} - {formatDate(team.hackathon.endDate)} at {formatTime(team.hackathon.endDate)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg">
                    <h4 className="font-bold text-red-900 mb-3 text-lg flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                      Hackathon Information Unavailable
                    </h4>
                    <p className="text-sm text-red-700">
                      The hackathon information for this team could not be loaded. This might be due to a deleted hackathon or data inconsistency.
                    </p>
                  </div>
                )}

                {/* Problem Statement */}
                <div className="mb-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl shadow-sm">
                  <h5 className="font-bold text-blue-900 mb-4 flex items-center gap-2 text-xl">
                    <AlertCircle className="h-6 w-6" />
                    Problem Statement
                  </h5>
                  {team.assignedProblemStatement ? (
                    <div>
                      <div className="mb-4">
                        <h6 className="text-lg font-bold text-blue-900 mb-3 border-b border-blue-200 pb-2">
                          {team.assignedProblemStatement.title}
                        </h6>
                        <div className="bg-white p-3 rounded-lg border border-blue-100">
                          <p className="text-sm text-blue-800 leading-relaxed whitespace-pre-wrap">
                            {team.assignedProblemStatement.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          ✓ Assigned by Admin
                        </Badge>
                        {team.assignedProblemStatement.category && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">
                            {team.assignedProblemStatement.category}
                          </Badge>
                        )}
                        {team.assignedProblemStatement.difficulty && (
                          <Badge className={`text-xs ${
                            team.assignedProblemStatement.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                            team.assignedProblemStatement.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {team.assignedProblemStatement.difficulty.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) : team.selectedProblemStatement ? (
                    <div>
                      <div className="mb-4">
                        <h6 className="text-lg font-bold text-blue-900 mb-3 border-b border-blue-200 pb-2">
                          {team.selectedProblemStatement.title}
                        </h6>
                        <div className="bg-white p-3 rounded-lg border border-blue-100">
                          <p className="text-sm text-blue-800 leading-relaxed whitespace-pre-wrap">
                            {team.selectedProblemStatement.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-blue-100 text-blue-800 text-xs">
                          ⏳ Selected by Leader
                        </Badge>
                        {team.selectedProblemStatement.category && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs">
                            {team.selectedProblemStatement.category}
                          </Badge>
                        )}
                        {team.selectedProblemStatement.difficulty && (
                          <Badge className={`text-xs ${
                            team.selectedProblemStatement.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                            team.selectedProblemStatement.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {team.selectedProblemStatement.difficulty.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-sm text-blue-600 font-medium">No problem statement selected yet</p>
                      <p className="text-xs text-blue-500 mt-1">
                        {isTeamLeader(team) ? 'Select a problem statement to get started' : 'Waiting for leader to select problem statement'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Team Members */}
                <div className="mb-4">
                  <h5 className="font-medium text-gray-900 mb-2">Team Members ({team.members.length})</h5>
                  <div className="space-y-2">
                    {team.members.map((member, index) => (
                      <div key={member.user._id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded-md">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            member.role === 'leader' ? 'bg-blue-500' : 'bg-gray-400'
                          }`}></div>
                          <span className={`font-medium ${
                            member.role === 'leader' ? 'text-blue-900' : 'text-gray-700'
                          }`}>
                            {member.user.firstName} {member.user.lastName}
                          </span>
                          {member.role === 'leader' && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs">👑 Leader</Badge>
                          )}
                        </div>
                        {member.role !== 'leader' && (
                          <span className="text-xs text-gray-500">Member</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>



                {/* Submission Status */}
                {team.submission && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <h5 className="font-medium text-green-900 mb-1">Project Submitted</h5>
                    <p className="text-sm text-green-700">
                      Submitted on {formatDate(team.submission.submittedAt)}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Link to={`/teams/${team._id}`}>
                    <Button size="sm" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">
                      <Eye className="h-4 w-4 mr-1" />
                      View Details
                    </Button>
                  </Link>
                  {/* Delete button for team leaders when team is rejected or forming */}
                  {isTeamLeader(team) && (team.status === 'rejected' || team.status === 'forming') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTeam(team._id, team.name)}
                      className="text-red-600 hover:text-red-700 border-red-600"
                      title="Delete Team"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete the team "{teamToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTeam}>
              Delete Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTeams; 